/**
 * Сервис для работы с CryptoPro Web Extension SDK
 * Использует модуль cadesplugin для доступа к API
 */
import { cadesplugin } from './cadesplugin';
import type { Certificate, CertificateInfo, WorkplaceStatus, SignOptions } from '../models/digitalSignatures/cryptoProTypes';
export type { Certificate, CertificateInfo, WorkplaceStatus, SignOptions };

class CryptoProService {
    // Флаг для включения debug логов при подписании (установить в true для отладки)
    private readonly ENABLE_DEBUG_LOGS = false;
    
    private readonly STORE_TYPE = cadesplugin.CADESCOM_CONTAINER_STORE;
    private readonly STORE_NAME = '';
    private readonly STORE_OPEN_MODE = cadesplugin.CAPICOM_STORE_OPEN_MAXIMUM_ALLOWED;

    private readonly PLUGIN_URL =
        'https://docs.cryptopro.ru/cades/plugin/plugin-installation-windows?id=%d0%a3%d1%81%d1%82%d0%b0%d0%bd%d0%be%d0%b2%d0%ba%d0%b0-%d1%80%d0%b0%d1%81%d1%88%d0%b8%d1%80%d0%b5%d0%bd%d0%b8%d1%8f-%d0%b1%d1%80%d0%b0%d1%83%d0%b7%d0%b5%d1%80%d0%b0';
    private readonly CSP_URL = 'https://cryptopro.ru/downloads';
    private readonly LICENSE_URL = 'https://cryptopro.ru/buy';

    private readonly LICENSE_REQUIRED_MESSAGE =
        'Отсутствует лицензия КриптоПро. Приобретите лицензию и повторите попытку.';
    private readonly EXTENSION_DISCONNECTED_MESSAGE =
        'Отсутствует или отключено браузерное расширение CryptoPro Extension for CAdES Browser Plug-in';
    private readonly CSP_NOT_INSTALLED_MESSAGE = 'Не установлено ПО КриптоПРО';
    private readonly PERMISSION_DENIED_MESSAGE =
        'Не выдано разрешение на использование ключей. Для корректной работы необходимо разрешить веб-сайту выполнение операций с ключами и сертификатами.';
    private readonly USB_KEY_NOT_FOUND_MESSAGE =
        'Не найден USB ключ для подписания. Установите ключ и перезагрузите страницу';
    private readonly USB_KEY_CONNECTION_ERROR_MESSAGE =
        'Ошибка связи с USB ключом.\nПопробуйте перезагрузить страницу.\nЕсли не помогло, попробуйте вынуть и вставить ключ заново, и затем перезагрузить страницу.';
    /** Единый текст для баннера «рабочее место не готово» со ссылками на плагин и CSP */
    private get WORKPLACE_NOT_READY_MESSAGE(): string {
        return (
            'Для использования физической ЭЦП необходимо:<br>' +
            `- <a href="${this.LICENSE_URL}" target="_blank" rel="noopener noreferrer">Лицензия КриптоПро CSP</a><br>` +
            `- <a href="${this.PLUGIN_URL}" target="_blank" rel="noopener noreferrer">Установить плагин браузера</a><br>` +
            `- <a href="${this.CSP_URL}" target="_blank" rel="noopener noreferrer">Установить КриптоПро CSP</a><br>` +
            'После установки ПО необходимо нажать кнопку "Проверить снова" или перезагрузить страницу или выйти и зайти в личный кабинет заново.'
        );
    }

    constructor() {
        // Устанавливаем уровень логирования DEBUG только если включен флаг ENABLE_DEBUG_LOGS
        if (this.ENABLE_DEBUG_LOGS) {
            try {
                if (cadesplugin && cadesplugin.set_log_level && cadesplugin.LOG_LEVEL_DEBUG) {
                    cadesplugin.set_log_level(cadesplugin.LOG_LEVEL_DEBUG);
                    console.info('CryptoPro log level установлен в DEBUG');
                }
            } catch (error) {
                console.warn('Не удалось установить log level для CryptoPro:', error);
            }
        }
    }

    /**
     * Проверка готовности рабочего места к подписанию.
     * Собирает все ошибки в поле errors; isReady = false при любой ошибке.
     */
    async checkWorkplaceReadiness(): Promise<WorkplaceStatus> {
        console.info('[Проверка рабочего места] Начало проверки готовности рабочего места к подписанию');

        console.info('[Проверка рабочего места] Этап 1: Проверка установки плагина браузера и CSP');
        const { isPluginOk, isCspOk } = await this.checkPluginAndCsp();

        let isLicenseOk = false;
        if (isPluginOk) {
            console.info('[Проверка рабочего места] Этап 2: Проверка лицензии CSP через CAdESCOM.CPLicense');
            isLicenseOk = await this.checkLicense();
        } else {
            console.info('[Проверка рабочего места] Этап 2: пропущен (нет плагина — лицензию проверить нельзя)');
        }

        const isReady = isPluginOk && isCspOk && isLicenseOk;
        if (isReady) {
            console.info('[Проверка рабочего места] Все этапы пройдены успешно. Рабочее место готово к подписанию ✓');
        }
        const errors = isReady ? '' : this.WORKPLACE_NOT_READY_MESSAGE;
        return { isReady, errors };
    }

    private async checkPluginAndCsp(): Promise<{ isPluginOk: boolean; isCspOk: boolean }> {
        try {
            const oAbout = await cadesplugin.CreateObjectAsync('CAdESCOM.About');
            if (!oAbout) return { isPluginOk: false, isCspOk: false };
            const version = await oAbout.Version;
            console.info('[Проверка рабочего места] Этап 1: Плагин браузера установлен ✓. Версия:', version);
            const isCspOk = await this.checkCsp(oAbout);
            return { isPluginOk: true, isCspOk };
        } catch (error: any) {
            console.error('[Проверка рабочего места] Ошибка при проверке плагина:', error);
            return { isPluginOk: false, isCspOk: false };
        }
    }

    private async checkCsp(oAbout: any): Promise<boolean> {
        try {
            const sCSPName = await oAbout.CSPName(80);
            const cspVersion = await oAbout.CSPVersion();
            const majorVersion = await cspVersion.MajorVersion;
            const minorVersion = await cspVersion.MinorVersion;
            const buildVersion = await cspVersion.BuildVersion;
            console.info(`[Проверка рабочего места] CSP доступен ✓. CSP: ${sCSPName}, версия: ${majorVersion}.${minorVersion}.${buildVersion}`);
            return true;
        } catch (error: any) {
            console.error('[Проверка рабочего места] Ошибка при проверке CSP:', error);
            return false;
        }
    }

    private async checkLicense(): Promise<boolean> {
        try {
            const license = await cadesplugin.CreateObjectAsync('CAdESCOM.CPLicense');
            if (!license || !(await license.IsValid())) return false;
            const validTo = await license.ValidTo();
            const licenseType = await license.Type(0);
            console.info(`[Проверка рабочего места] Этап 2: Лицензия CSP действительна ✓. Тип: ${licenseType ?? '—'}, валидна до: ${validTo ?? '—'}`);
            return true;
        } catch (error: any) {
            console.error('[Проверка рабочего места] Ошибка при проверке лицензии:', error);
            return false;
        }
    }

    /**
     * Нормализация thumbprint (убирает двоеточия, пробелы и приводит к верхнему регистру)
     */
    private normalizeThumbprint(thumbprint: string): string {
        return (thumbprint || '').replace(/:/g, '').replace(/\s/g, '').toUpperCase();
    }

    /**
     * Сбор данных сертификата и преобразование в формат Certificate
     */
    private async collectCertificateData(cert: any): Promise<Certificate | null> {
        try {
            const hasPrivateKey = await cert.HasPrivateKey();
            if (!hasPrivateKey) {
                return null;
            }

            const thumbprint = await cert.Thumbprint;
            const subjectName = await cert.SubjectName;
            const validToDate = await cert.ValidToDate;
            const validFromDate = await cert.ValidFromDate;
            const issuerName = await cert.IssuerName;

            return {
                thumbprint: this.normalizeThumbprint(thumbprint),
                subject: subjectName || '',
                validFrom: validFromDate ? new Date(validFromDate) : new Date(),
                validTo: validToDate ? new Date(validToDate) : new Date(),
                serialNumber: undefined,
                issuer: issuerName || '',
            };
        } catch (error) {
            return null;
        }
    }

    /**
     * Получение списка сертификатов с закрытым ключом из Rutoken USB
     */
    async enumerateCertificates(): Promise<Certificate[]> {
        const result: any[] = [];

        try {
            
            // Открываем только хранилище смарт-карт (Rutoken USB)
            const store = await cadesplugin.CreateObjectAsync('CAdESCOM.Store');
            await new Promise(resolve => setTimeout(resolve, 500)); // Ждём 0.5 сек до готовности хранилища
            await store.Open(this.STORE_TYPE, this.STORE_NAME, this.STORE_OPEN_MODE);
            const certs = await store.Certificates;
            
            // Получаем количество сертификатов
            const count = await certs.Count;
            
            // Перебираем каждый сертификат (нумерация с 1, как в COM API)
            for (let i = 1; i <= count; i++) {
                const cert = await certs.Item(i);
                const certData = await this.collectCertificateData(cert);
                
                if (certData) {
                    // На некоторых токенах один и тот же сертификат может возвращаться несколько раз,
                    // поэтому удаляем дубликаты по отпечатку
                    const exists = result.some((c) => c.thumbprint === certData.thumbprint);
                    if (!exists) {
                        result.push(certData);
                    }
                }
            }
            
        } catch (error: any) {
            console.error('Ошибка получения списка сертификатов:', error);
        }
        return result;
    }

    /**
     * Получение сертификата по thumbprint из хранилища смарт-карт
     * Возвращает COM объект сертификата
     * Хранилище НЕ закрывается, чтобы избежать ошибки "More data is available" при повторном подписании
     */
    private async getCertificateByThumbprint(thumbprint: string): Promise<any> {
        try {
            const store = await cadesplugin.CreateObjectAsync('CAdESCOM.Store');
            await new Promise(resolve => setTimeout(resolve, 500)); // Ждём 0.5 сек до готовности хранилища
            await store.Open(this.STORE_TYPE, this.STORE_NAME, this.STORE_OPEN_MODE);
            const certs = await store.Certificates;

            const foundCerts = await certs.Find(cadesplugin.CAPICOM_CERTIFICATE_FIND_SHA1_HASH, thumbprint);
            const count = await foundCerts.Count;

            if (count === 0) {
                throw new Error('Не найден ключ для подписания');
            }

            const cert = await foundCerts.Item(1);
            // Хранилище НЕ закрываем - оставляем открытым для повторного использования
            return cert;
        } catch (error: any) {
            console.error('Ошибка получения сертификата по thumbprint:', error);
            throw error;
        }
    }

    /**
     * Получение информации о сертификате
     */
    async getCertificateInfo(thumbprint: string): Promise<CertificateInfo> {
        try {
            const cert = await this.getCertificateByThumbprint(thumbprint);

            const subjectName = await cert.SubjectName;
            const issuerName = await cert.IssuerName;
            const validFromDate = await cert.ValidFromDate;
            const validToDate = await cert.ValidToDate;
            const serialNumber = await cert.SerialNumber;

            return {
                subject: subjectName || '',
                validFrom: validFromDate ? new Date(validFromDate) : new Date(),
                validTo: validToDate ? new Date(validToDate) : new Date(),
                serialNumber: serialNumber || '',
                issuer: issuerName || '',
                thumbprint: this.normalizeThumbprint(thumbprint),
            };
        } catch (error: any) {
            console.error('Ошибка получения информации о сертификате:', error);
            throw error;
        }
    }

    /**
     * Подписание данных
     * @param thumbprint - Отпечаток сертификата
     * @param data - Данные для подписания (base64 строка)
     * @param options - Опции подписания
     * @returns Подпись в base64
     */
    async sign(thumbprint: string, data: string, options: SignOptions): Promise<string> {
        if (this.ENABLE_DEBUG_LOGS) {
            console.info('[Подписание] Начало подписания данных. Thumbprint:', thumbprint);
        }
        
        let cert: any = null;
        let signer: any = null;
        let signedData: any = null;

        try {
            if (options.detached) {
                if (this.ENABLE_DEBUG_LOGS) {
                    console.info('[Подписание] Этап 1: Получение сертификата по thumbprint');
                }
                cert = await this.getCertificateByThumbprint(thumbprint);

                if (this.ENABLE_DEBUG_LOGS) {
                    console.info('[Подписание] Этап 2: Проверка наличия закрытого ключа');
                }
                const hasPrivateKey = await cert.HasPrivateKey();
                if (!hasPrivateKey) {
                    throw new Error('Не найден ключ для подписания');
                }
                if (this.ENABLE_DEBUG_LOGS) {
                    console.info('[Подписание] Этап 2: Закрытый ключ найден ✓');
                }

                if (this.ENABLE_DEBUG_LOGS) {
                    console.info('[Подписание] Этап 3: Создание объекта подписанта');
                }
                signer = await cadesplugin.CreateObjectAsync('CAdESCOM.CPSigner');
                await signer.propset_Certificate(cert);
                if (this.ENABLE_DEBUG_LOGS) {
                    console.info('[Подписание] Этап 3: Объект подписанта создан ✓');
                }

                if (this.ENABLE_DEBUG_LOGS) {
                    console.info('[Подписание] Этап 4: Создание объекта подписанных данных');
                }
                signedData = await cadesplugin.CreateObjectAsync('CAdESCOM.CadesSignedData');
                await signedData.propset_ContentEncoding(cadesplugin.CADESCOM_BASE64_TO_BINARY);
                await signedData.propset_Content(data);
                if (this.ENABLE_DEBUG_LOGS) {
                    console.info('[Подписание] Этап 4: Объект подписанных данных создан ✓');
                }

                if (this.ENABLE_DEBUG_LOGS) {
                    console.info('[Подписание] Этап 5: Выполнение подписания');
                }
                const signature = await signedData.SignCades(signer, cadesplugin.CADESCOM_CADES_BES, true);
                if (this.ENABLE_DEBUG_LOGS) {
                    console.info('[Подписание] Подписание выполнено успешно ✓. Размер подписи:', signature?.length || 0, 'символов');
                }
                return signature;
            } else {
                throw new Error('Присоединенная подпись не поддерживается в текущей реализации');
            }
        } catch (error: any) {
            console.error('[Подписание] Ошибка при подписании:', error);
            throw error;
        } finally {
            try {
                if (signedData) {
                    signedData = null;
                }
                if (signer) {
                    signer = null;
                }
                if (cert) {
                    cert = null;
                }
            } catch (cleanupError) {
                console.warn('Ошибка при освобождении COM объектов:', cleanupError);
            }
        }
    }

    /**
     * Нормализация ошибок CryptoPro
     * @param error - Ошибка для нормализации
     * @param context - Контекст использования (для кастомизации сообщений)
     * @returns Нормализованное сообщение об ошибке
     */
    normalizeError(error: any, context?: string): string {
        const errorMessage = error.message || String(error);
        const errorCode = error.code || error.number || '';
        const errorString = String(errorCode);
        
        // Ошибка "More data is available" (0x800700EA) - проблема с чтением данных с USB токена
        // Может возникать при временных проблемах связи с токеном
        if (errorString.includes('0x800700EA') 
            || errorString.includes('800700EA')
            || errorMessage.includes('More data is available')
            || errorMessage.includes('0x800700EA')) {
            return this.USB_KEY_CONNECTION_ERROR_MESSAGE;
        }
        
        // Ошибка о не найденном ключе (токен не вставлен или нет закрытого ключа)
        if (error.message === 'DEVICE_NOT_FOUND' 
            || errorMessage.includes('не найден') 
            || errorMessage.includes('Сертификат не найден')
            || errorMessage.includes('не имеет закрытого ключа')
            || errorMessage.includes('HasPrivateKey')
            || errorMessage.includes('Не найден ключ')
            || errorMessage.includes('Сертификаты с закрытым ключом не найдены')) {
            return this.USB_KEY_NOT_FOUND_MESSAGE;
        }

        // Отключено или отсутствует браузерное расширение КриптоПро
        if (errorMessage.toLowerCase().includes('lost connection to extension')) {
            return this.EXTENSION_DISCONNECTED_MESSAGE;
        }

        // Не установлено ПО КриптоПро CSP (тип поставщика не определен)
        const msgLower = errorMessage.toLowerCase();
        if (
            msgLower.includes('тип поставщика не определен') ||
            errorString.includes('80090017') ||
            msgLower.includes('0x80090017') ||
            msgLower.includes('0х80090017')
        ) {
            return this.CSP_NOT_INSTALLED_MESSAGE;
        }

        // Пользователь запретил веб-сайту операции с ключами в диалоге КриптоПро
        if (
            msgLower.includes('операция была отменена пользователем') ||
            errorString.includes('0x000004C7') ||
            errorString.includes('0x4C7') ||
            errorString.includes('4C7') ||
            errorMessage.includes('отменена пользователем')
        ) {
            return this.PERMISSION_DENIED_MESSAGE;
        }

        // Сообщение уже от checkWorkplaceReadiness (полный текст с ссылками) — не перезаписываем
        if (errorMessage.includes('Для использования физической ЭЦП необходимо')) {
            return errorMessage;
        }

        // Ошибка об отсутствии или истечении лицензии КриптоПро CSP
        if (msgLower.includes('лицензи')
            || msgLower.includes('серийный номер')
            || (msgLower.includes('срок действия') && (msgLower.includes('криптопро') || msgLower.includes('csp')))
            || errorString.includes('0x80090016')
            || errorString.includes('80090016')) {
            return this.LICENSE_REQUIRED_MESSAGE;
        }

        // Ошибка о неготовности рабочего места (плагин или CSP не установлены)
        // Если сообщение уже содержит полный текст о неготовности рабочего места, возвращаем его как есть
        if (errorMessage.includes('Рабочее место не готово')) {
            return errorMessage;
        }

        if (error.message === 'PLUGIN_NOT_INSTALLED' 
            || errorMessage.includes('Плагин CryptoPro недоступен')
            || errorMessage.includes('CryptoPro CSP не доступен')
            || errorMessage.includes('плагин') 
            || errorMessage.includes('plugin') 
            || errorMessage.includes('CAdESCOM')
            || errorMessage.includes('CreateObjectAsync')) {
            return this.WORKPLACE_NOT_READY_MESSAGE;
        }

        return error.response?.data?.message ?? error.message ?? 'Непредвиденная ошибка';
    }
}


export const cryptoProService = new CryptoProService();
